import React, { useEffect, useMemo, useState } from "react";
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Table, Spinner, Alert, Badge, Input,
    Accordion, AccordionItem, AccordionHeader, AccordionBody
} from "reactstrap";
import Session from "../auth/Session";

function authHeaders() {
    const token = Session.getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
}

// Entity-type metadata: drives section rendering. `allowCreate` = unmatched QBO rows
// can produce a new local row (false for TransactionType per spec).
const SECTIONS = [
    { key: 'customers',        label: 'Customers',         allowCreate: true  },
    { key: 'subCustomers',     label: 'Sub-Customers',     allowCreate: true  },
    { key: 'drivers',          label: 'Drivers / Vendors', allowCreate: true  },
    { key: 'transactionTypes', label: 'Transaction Types', allowCreate: false },
    { key: 'routes',           label: 'Routes',            allowCreate: false },
    { key: 'checkingItems',    label: 'Checking Items',    allowCreate: false },
    { key: 'classes',          label: 'Classes',           allowCreate: false },
];

export const QBO_PULL_SECTIONS = SECTIONS;

// Sections whose QBO record is a QBO Item (id lives on `qboItemId`).
const ITEM_SECTIONS = new Set(['transactionTypes', 'routes', 'checkingItems']);

// Sections that link to a local transaction type (so they share the transactionTypeTargets list).
const TT_TARGET_SECTIONS = new Set(['transactionTypes', 'checkingItems']);

function qboIdOf(row, sectionKey) {
    // Item-backed sections use QboItemId in the DTO; others use QboId
    return ITEM_SECTIONS.has(sectionKey) ? row.qboItemId : row.qboId;
}

function qboLabelOf(row, sectionKey) {
    if (ITEM_SECTIONS.has(sectionKey)) return row.qboName || '(unnamed)';
    if (sectionKey === 'drivers') return row.qboDisplayName || row.qboCompanyName || '(unnamed)';
    return row.qboName || '(unnamed)';
}

function localLabelOf(row) {
    return row.localName || (row.localId ? `#${row.localId}` : '—');
}

// Section-scoped modal: previews and applies a single QBO entity grouping.
function QboPullPreviewModal({ isOpen, toggle, sectionKey }) {
    const section = useMemo(() => SECTIONS.find(s => s.key === sectionKey) || null, [sectionKey]);

    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    // When true, records the user previously chose to skip are included in the preview
    // (otherwise they are hidden by the server).
    const [showSkipped, setShowSkipped] = useState(false);
    // Bumped after a successful apply to force the preview to reload.
    const [reloadKey, setReloadKey] = useState(0);
    // selections shape: { [qboId]: { action: 'Link'|'Create'|'Skip', localId?: int, role?, locked?: bool } }
    const [selections, setSelections] = useState({});
    // Which result panels are expanded. All open by default; each toggles independently.
    const [openPanels, setOpenPanels] = useState(['matched', 'unmatched', 'localOnly']);

    const togglePanel = (id) => {
        setOpenPanels(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
    };

    // Clear transient state when the modal is (re)opened.
    useEffect(() => {
        if (!isOpen) return;
        setResult(null);
        setShowSkipped(false);
    }, [isOpen, sectionKey]);

    useEffect(() => {
        if (!isOpen || !section) return;
        setError(null);
        setPreview(null);
        setSelections({});
        setLoading(true);
        fetch(`api/qbo/pull/preview?section=${encodeURIComponent(section.key)}&includeSkipped=${showSkipped}`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(data => {
                setPreview(data);
                // initialize default action per row. Already-skipped rows default to Skip so the
                // dropdown reflects the stored decision (and the user can flip it to unskip).
                // Otherwise: Link for matched, Create for unmatched (when allowed), else Skip.
                const init = {};
                const sec = data[section.key] || {};
                const isTt = section.key === 'transactionTypes';
                (sec.matched || []).forEach(row => {
                    const id = qboIdOf(row, section.key);
                    if (!id) return;
                    if (row.alreadyLinked) {
                        // already linked rows are not actionable; lock them out of apply.
                        init[id] = { action: 'Skip', role: row.matchedRole || 'Sales', locked: true };
                    } else if (row.isSkipped) {
                        init[id] = { action: 'Skip', localId: row.localId, role: row.matchedRole || 'Sales' };
                    } else {
                        init[id] = { action: 'Link', localId: row.localId, role: row.matchedRole || 'Sales' };
                    }
                });
                (sec.unmatched || []).forEach(row => {
                    const id = qboIdOf(row, section.key);
                    if (!id) return;
                    if (row.isSkipped) {
                        init[id] = { action: 'Skip', role: isTt ? 'Sales' : undefined };
                    } else {
                        init[id] = section.allowCreate
                            ? { action: 'Create' }
                            : { action: 'Skip', role: isTt ? 'Sales' : undefined };
                    }
                });
                setSelections(init);
            })
            .catch(e => setError(`Preview failed: ${e.message}`))
            .finally(() => setLoading(false));
    }, [isOpen, section, showSkipped, reloadKey]);

    const setRow = (qboId, patch) => {
        setSelections(prev => ({
            ...prev,
            [qboId]: { ...(prev[qboId] || {}), ...patch }
        }));
    };

    // Apply one decision to every actionable row in the section. Each decision is only
    // applied where it makes sense: "Link" to rows that already have a matched local
    // row, "Create" to unmatched rows (when the section allows it), "Skip" to all.
    const setSectionAll = (decision) => {
        if (!decision || !preview || !section) return;
        const sec = preview[section.key] || {};
        const rows = [...(sec.matched || []), ...(sec.unmatched || [])];
        setSelections(prev => {
            const next = { ...prev };
            rows.forEach(row => {
                const id = qboIdOf(row, section.key);
                if (!id) return;
                const cur = next[id] || {};
                if (cur.locked) return; // already-linked rows are untouched
                if (decision === 'Link') {
                    if (!row.localId) return; // can't bulk-pick a target for unmatched rows
                    next[id] = { ...cur, action: 'Link', localId: row.localId };
                } else if (decision === 'Create') {
                    if (!section.allowCreate || row.localId) return; // unmatched only
                    next[id] = { ...cur, action: 'Create', localId: null };
                } else if (decision === 'Skip') {
                    next[id] = { ...cur, action: 'Skip' };
                }
            });
            return next;
        });
    };

    // Local rows available as link targets. For transaction-type-backed sections we offer
    // every local type (not just unlinked ones), since a type can carry both a sales item
    // and a separate bill/checking item.
    const linkTargets = useMemo(() => {
        if (!preview || !section) return [];
        if (TT_TARGET_SECTIONS.has(section.key)) {
            return (preview.transactionTypeTargets || []).map(t => ({
                localId: t.id,
                localName: t.name,
                hasSalesItem: t.hasSalesItem,
                hasBillItem: t.hasBillItem
            }));
        }
        return (preview[section.key]?.localOnly || []).filter(r => r.localId);
    }, [preview, section]);

    const onApply = async () => {
        if (!section) return;
        setApplying(true);
        setError(null);
        try {
            const body = { customers: [], subCustomers: [], drivers: [], transactionTypes: [], routes: [], checkingItems: [], classes: [] };
            Object.entries(selections).forEach(([qboId, sel]) => {
                if (!sel || sel.locked) return; // already-linked rows are not sent
                // Link needs a target; without one there's nothing to apply.
                if (sel.action === 'Link' && !sel.localId) return;
                const entry = { qboId, action: sel.action, localId: sel.localId ?? null };
                if (section.key === 'transactionTypes') entry.role = sel.role || 'Sales';
                body[section.key].push(entry);
            });
            const resp = await fetch('api/qbo/pull/apply', {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(body)
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            setResult(await resp.json());
            // Reload so persisted skips/links drop out of (or update in) the list.
            setReloadKey(k => k + 1);
        } catch (e) {
            setError(`Apply failed: ${e.message}`);
        } finally {
            setApplying(false);
        }
    };

    const renderMatched = (rows) => {
        if (!rows?.length) return <div className="text-muted small">No matches.</div>;
        const showRoleBadge = TT_TARGET_SECTIONS.has(section.key);
        return (
            <Table size="sm" striped>
                <thead>
                    <tr>
                        <th>QBO record</th>
                        <th>Local record</th>
                        <th>Status</th>
                        <th style={{ width: 140 }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => {
                        const id = qboIdOf(row, section.key);
                        const sel = selections[id] || {};
                        return (
                            <tr key={`m-${id}`}>
                                <td>
                                    {qboLabelOf(row, section.key)}
                                    <div className="text-muted small">QBO #{id}{row.matchedBy ? ` · matched by ${row.matchedBy}` : ''}</div>
                                </td>
                                <td>
                                    {localLabelOf(row)}
                                    <div className="text-muted small">Local #{row.localId}</div>
                                </td>
                                <td>
                                    {row.alreadyLinked
                                        ? <Badge color="success">Linked</Badge>
                                        : <Badge color="info">Proposed link</Badge>}
                                    {row.isSkipped && <span className="ms-1"><Badge color="dark">Skipped</Badge></span>}
                                    {showRoleBadge && row.matchedRole && (
                                        <span className="ms-1">
                                            <Badge color={row.matchedRole === 'Bill' ? 'warning' : 'secondary'}>
                                                {row.matchedRole === 'Bill' ? 'Bill' : 'Sales'}
                                            </Badge>
                                        </span>
                                    )}
                                </td>
                                <td>
                                    <Input
                                        type="select"
                                        bsSize="sm"
                                        value={sel.action || 'Skip'}
                                        onChange={e => setRow(id, { action: e.target.value, localId: row.localId })}
                                        disabled={row.alreadyLinked}
                                    >
                                        <option value="Link">Link</option>
                                        <option value="Skip">Skip</option>
                                    </Input>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        );
    };

    const renderUnmatched = (rows) => {
        if (!rows?.length) return <div className="text-muted small">No unmatched QBO records.</div>;
        const isTt = section.key === 'transactionTypes';
        const usesTtTargets = TT_TARGET_SECTIONS.has(section.key);
        return (
            <Table size="sm" striped>
                <thead>
                    <tr>
                        <th>QBO record</th>
                        <th style={{ width: 140 }}>Action</th>
                        {isTt && <th style={{ width: 130 }}>Link as</th>}
                        <th>Link target (if Link)</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => {
                        const id = qboIdOf(row, section.key);
                        const sel = selections[id] || {};
                        const role = sel.role || 'Sales';
                        return (
                            <tr key={`u-${id}`}>
                                <td>
                                    {qboLabelOf(row, section.key)}
                                    {row.isSkipped && <span className="ms-1"><Badge color="dark">Skipped</Badge></span>}
                                    <div className="text-muted small">QBO #{id}{usesTtTargets ? ` · type: ${row.qboType || '—'}` : ''}</div>
                                </td>
                                <td>
                                    <Input
                                        type="select"
                                        bsSize="sm"
                                        value={sel.action || 'Skip'}
                                        onChange={e => setRow(id, { action: e.target.value, localId: e.target.value === 'Link' ? sel.localId : null })}
                                    >
                                        {section.allowCreate && <option value="Create">Create local</option>}
                                        <option value="Link">Link to existing</option>
                                        <option value="Skip">Skip</option>
                                    </Input>
                                </td>
                                {isTt && (
                                    <td>
                                        {sel.action === 'Link' ? (
                                            <Input
                                                type="select"
                                                bsSize="sm"
                                                value={role}
                                                onChange={e => setRow(id, { role: e.target.value })}
                                            >
                                                <option value="Sales">Sales item</option>
                                                <option value="Bill">Bill item</option>
                                            </Input>
                                        ) : (
                                            <span className="text-muted small">—</span>
                                        )}
                                    </td>
                                )}
                                <td>
                                    {sel.action === 'Link' ? (
                                        <Input
                                            type="select"
                                            bsSize="sm"
                                            value={sel.localId || ''}
                                            onChange={e => setRow(id, { localId: parseInt(e.target.value, 10) || null })}
                                        >
                                            <option value="">— pick a local row —</option>
                                            {linkTargets.map(t => (
                                                <option key={t.localId} value={t.localId}>
                                                    #{t.localId} {t.localName || ''}
                                                    {usesTtTargets ? ` ${t.hasSalesItem ? '·S' : ''}${t.hasBillItem ? '·B' : ''}` : ''}
                                                </option>
                                            ))}
                                        </Input>
                                    ) : (
                                        <span className="text-muted small">—</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        );
    };

    const renderLocalOnly = (rows) => {
        if (!rows?.length) return <div className="text-muted small">No local-only records.</div>;
        const isTt = TT_TARGET_SECTIONS.has(section.key);
        return (
            <Table size="sm" striped>
                <thead>
                    <tr>
                        <th>Local record</th>
                        <th>Details</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => (
                        <tr key={`l-${row.localId}`}>
                            <td>{localLabelOf(row)}</td>
                            <td className="text-muted small">
                                Local #{row.localId}
                                {isTt && row.localQuickBooksType
                                    ? ` · QuickBooksType: ${row.localQuickBooksType}` : ''}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    const renderBody = () => {
        if (!section) return <Alert color="danger">Unknown section.</Alert>;
        const sec = preview?.[section.key] || {};
        // Split matched rows: proposed links the user still needs to act on vs. records already
        // linked to a local record (non-actionable). The already-linked group collapses by default
        // so a large backlog of linked records doesn't bury the unlinked matches.
        const matchedProposed = (sec.matched || []).filter(r => !r.alreadyLinked);
        const matchedLinked = (sec.matched || []).filter(r => r.alreadyLinked);
        return (
            <>
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div className="form-check mb-0">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id={`qbo-show-skipped-${section.key}`}
                            checked={showSkipped}
                            onChange={e => setShowSkipped(e.target.checked)}
                            disabled={loading || applying}
                        />
                        <label className="form-check-label" htmlFor={`qbo-show-skipped-${section.key}`}>
                            Show skipped records
                        </label>
                    </div>
                    <div className="d-flex align-items-center">
                        <span className="text-muted small me-2">Set all:</span>
                        <Input
                            type="select"
                            bsSize="sm"
                            style={{ width: 'auto' }}
                            value=""
                            disabled={loading || applying || !preview}
                            onChange={e => { setSectionAll(e.target.value); e.target.value = ''; }}
                        >
                            <option value="">— choose —</option>
                            <option value="Link">Link all (matched)</option>
                            {section.allowCreate && <option value="Create">Create all (unmatched)</option>}
                            <option value="Skip">Skip all</option>
                        </Input>
                    </div>
                </div>

                {loading && <div><Spinner size="sm" /> Loading preview…</div>}

                {!loading && preview && (
                    <Accordion open={openPanels} toggle={togglePanel} stayOpen>
                        <AccordionItem>
                            <AccordionHeader targetId="matched">
                                Needs linking{' '}
                                <Badge color="secondary" className="ms-2">{matchedProposed.length}</Badge>
                            </AccordionHeader>
                            <AccordionBody accordionId="matched">
                                {renderMatched(matchedProposed)}
                            </AccordionBody>
                        </AccordionItem>
                        <AccordionItem>
                            <AccordionHeader targetId="alreadyLinked">
                                Already linked{' '}
                                <Badge color="secondary" className="ms-2">{matchedLinked.length}</Badge>
                            </AccordionHeader>
                            <AccordionBody accordionId="alreadyLinked">
                                {renderMatched(matchedLinked)}
                            </AccordionBody>
                        </AccordionItem>
                        <AccordionItem>
                            <AccordionHeader targetId="unmatched">
                                Unmatched (QBO only){' '}
                                <Badge color="secondary" className="ms-2">{sec.unmatched?.length || 0}</Badge>
                            </AccordionHeader>
                            <AccordionBody accordionId="unmatched">
                                {renderUnmatched(sec.unmatched)}
                            </AccordionBody>
                        </AccordionItem>
                        <AccordionItem>
                            <AccordionHeader targetId="localOnly">
                                Local only{' '}
                                <Badge color="secondary" className="ms-2">{sec.localOnly?.length || 0}</Badge>
                            </AccordionHeader>
                            <AccordionBody accordionId="localOnly">
                                {renderLocalOnly(sec.localOnly)}
                            </AccordionBody>
                        </AccordionItem>
                    </Accordion>
                )}
            </>
        );
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
            <ModalHeader toggle={toggle}>
                Preview QuickBooks pull{section ? ` — ${section.label}` : ''}
            </ModalHeader>
            <ModalBody>
                {error && <Alert color="danger">{error}</Alert>}
                {result && (
                    <Alert color={result.failed > 0 ? 'warning' : 'success'}>
                        Apply complete: <strong>{result.linked}</strong> linked,{' '}
                        <strong>{result.created}</strong> created,{' '}
                        <strong>{result.skipped}</strong> skipped,{' '}
                        <strong>{result.failed}</strong> failed.
                        {result.errors?.length > 0 && (
                            <ul className="mb-0 mt-2">
                                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        )}
                    </Alert>
                )}
                {renderBody()}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle} disabled={applying}>Close</Button>
                <Button color="primary" onClick={onApply} disabled={loading || applying || !preview}>
                    {applying ? <><Spinner size="sm" /> Applying…</> : 'Apply selections'}
                </Button>
            </ModalFooter>
        </Modal>
    );
}

export default QboPullPreviewModal;
